/**
 * Palantir PMS - Software para la gestión de la industria hotelera
 * Palantir PMS - Software for hotel industry management
 * Autor / Author: Alberto Castro Ull
 * Fecha de creación / Created on: 31/07/2025
 * Todos los derechos reservados / All rights reserved.
 * Este software es propiedad de Alberto Castro Ull.
 * This software is the property of Alberto Castro Ull.
 * Su uso está restringido a clientes con licencia emitida por Markulia Group, S.L.
 * Use is restricted to clients licensed by Markulia Group, S.L.
 */

import axios from "../api/axiosInstance";

export function useGuestManagementHandlers({
  guestCustomers,
  setGuestCustomers,
  guestChildren,
  setGuestChildren,
  setCustomerFormData,
  setShowNewCustomerForm,
  customerIdFromURL,
  showNewCustomerForm,
  editingChildIndex,
  customerFormData,
  setEditingChildIndex,
  businessUnitId,
  navigate,
  requiredCustomerFieldsReservation,
  setEditingAccompanyingIndex,
  searchParams
}) {

// ✅ Añadir acompañante nuevo (crear uno vacío)
  const handleAddNewAccompanyingGuest = (index) => {
    // Limpiar el formulario de acompañante anterior, si existe
    if (showNewCustomerForm && showNewCustomerForm.startsWith("accompanying")) {
      setShowNewCustomerForm(false); // Reseteamos el formulario de acompañante si hay alguno abierto
    }
  
    const newGuest = {
      first_name: "",
      last_name: "",
      middle_name: "",
      second_last_name: "",
      email: "",
      phone: "",
      address: "",
      postal_code: "",
      country: "",
      city: "",
      gender: "",
      birth_date: "",
      document_type: "",
      document_number: "",
      is_vip: false,
      vip_description: "",
      photo_url: "",
      level: "",
    };
  
    const updatedGuests = [...guestCustomers];
    updatedGuests[index] = newGuest;
    setGuestCustomers(updatedGuests);
    setCustomerFormData(newGuest);
    setShowNewCustomerForm(`accompanying-${index}`);
  console.log(`🧑‍🤝‍🧑 Acompañante #${index + 1} añadido localmente (aún no guardado).`);
  };

const handleAddNewChild = (index) => {
  // Limpiar el formulario de child anterior, si existe
  if (showNewCustomerForm && showNewCustomerForm.startsWith("child")) {
    setShowNewCustomerForm(false); // Reseteamos el formulario de niño si hay alguno abierto
  }

  const newChild = {
    first_name: "",
    last_name: "",
    middle_name: "",
    second_last_name: "",
    email: "",
    phone: "",
    address: "",
    postal_code: "",
    country: "",
    city: "",
    gender: "",
    birth_date: "",
    document_type: "",
    document_number: "",
    is_vip: false,
    vip_description: "",
    photo_url: "",
    level: "",
  };

  // 👇 Patón igual que acompañantes, pero usando guestChildren:
  const updatedChildren = [...guestChildren];
  updatedChildren[index] = newChild;
  setGuestChildren(updatedChildren);
  setCustomerFormData(newChild);
  setShowNewCustomerForm(`child-${index}`);
  console.log(`🧒 Niño #${index + 1} añadido localmente (aún no guardado).`);
};


const handleSelectChild = (index) => {
  const pathMatch = window.location.pathname.match(/\/new-reservation\/(\d+)/);
  const reservationId = pathMatch ? pathMatch[1] : null;

  if (!reservationId) {
    alert("Reservation ID missing");
    return;
  }

  const businessUnitId = searchParams.get("business_unit_id") || "1";

  console.log("NAVEGAR niño", { guestIndex: index });

  navigate(
    `/select-customer?business_unit_id=${businessUnitId}&reservation_id=${reservationId}`,
    {
      state: {
        mode: "child",
        guestIndex: index,
        customerIdFromURL
      }
    }
  );

};

const handleRemoveChild = async (index) => {
  const pathMatch = window.location.pathname.match(/\/new-reservation\/(\d+)/);
  const reservationId = pathMatch ? pathMatch[1] : null;
  const child = guestChildren[index];

  if (!reservationId) {
    alert("Reservation ID missing");
    return;
  }

  const customerId = child.customer_id || child.customer?.id || child.id;
  if (!customerId) {
    alert("Error: Could not determine customer ID for deletion.");
    console.error("❌ guestChild sin customer_id válido:", child);
    return;
  }

  try {
    const res = await axios.delete(
      `${process.env.REACT_APP_API_URL}/api/reservation-drafts/draft-guests/by-draft/${reservationId}/by-customer/${customerId}/child`,
      { params: { guest_index: index } }
    );

    console.log(`🗑️ Child #${index + 1} eliminado de reservation_draft_guests`, res.data);

    const updated = [...guestChildren];
    updated[index] = undefined;
    setGuestChildren(updated);

    console.log("🔁 guestChildren actualizado:", updated);
  } catch (error) {
    console.error("❌ Error removing child:", error);
    alert("Error removing child.");
  }

};

const handleSubmitAccompanyingGuest = async (index) => {
  const pathMatch = window.location.pathname.match(/\/new-reservation\/(\d+)/);
  const reservationId = pathMatch ? pathMatch[1] : null;
  const guest = guestCustomers[index];

  if (!reservationId || !guest) {
    alert("Missing reservation ID or guest data.");
    return;
  }

  // Validación de campos obligatorios desde settings
  const requiredFields = requiredCustomerFieldsReservation?.accompanying || [];
  const missingFields = requiredFields.filter(field => !guest[field] || guest[field].trim() === "");
  if (missingFields.length > 0) {
    alert(`Missing required fields for guest #${index + 1}: ${missingFields.join(", ")}`);
    return;
  }

  // Validación de edad mínima (adulto, según settings)
  try {
    const settingsRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/hotel-settings`, {
      params: { business_unit_id: businessUnitId }
    });
    const maxAgeChild = settingsRes.data.max_age_child;
    const birthDate = new Date(guest.birth_date);
    const age = Math.floor((new Date() - birthDate) / (365.25 * 24 * 60 * 60 * 1000));

    if (age <= maxAgeChild) {
      alert(`Guest #${index + 1} must be older than child max age (${maxAgeChild})`);
      return;
    }
  } catch (error) {
    console.error("❌ Error checking age:", error);
    alert("Error validating age for guest.");
    return;
  }

  try {
    const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/customers`, guest);
    const customerId = response.data.id;

    // 🔍 Verifica edad y decide si es niño o no
    let isChild = false;
    try {
      const settingsRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/hotel-settings`, {
        params: { business_unit_id: businessUnitId }
      });
      const minAgeChild = settingsRes.data.min_age_child;
      const maxAgeChild = settingsRes.data.max_age_child;
      const birthDate = new Date(guest.birth_date);
      const age = Math.floor((new Date() - birthDate) / (365.25 * 24 * 60 * 60 * 1000));
      isChild = age >= minAgeChild && age <= maxAgeChild;
    } catch (error) {
      // Ignora error secundario de settings
    }

    // 📩 Guardar en reservation_guests
    await axios.post(`${process.env.REACT_APP_API_URL}/api/reservation-drafts/draft-guests`, {
      reservation_draft_id: reservationId,
      customer_id: customerId,
      is_main_guest: false,
      is_child: false,
      guest_index: Number(index),
      relationship: null
    });


    // Actualiza el array local para reflejar el customer ya creado
    const updatedGuests = [...guestCustomers];
    updatedGuests[index] = { ...guest, id: customerId };
    setGuestCustomers(updatedGuests);

    // Limpia la edición/modal
    setEditingAccompanyingIndex(null);
    setShowNewCustomerForm(false);

    alert(`Guest #${index + 1} saved successfully.`);
  } catch (error) {
    console.error("❌ Error saving accompanying guest:", error);
    alert("Error saving accompanying guest.");
  }
};



const handleSubmitChild = async (index) => {
  const pathMatch = window.location.pathname.match(/\/new-reservation\/(\d+)/);
  const reservationId = pathMatch ? pathMatch[1] : null;
  const child = editingChildIndex !== null ? customerFormData : guestChildren[index];

  console.log("📦 Dato completo del niño (child):", child);
  console.log("📅 Fecha de nacimiento cruda:", child.birth_date);
  console.log("📅 Como objeto Date:", new Date(child.birth_date));
  console.log("🧮 isNaN(new Date(child.birth_date).getTime()):", isNaN(new Date(child.birth_date).getTime()));

  

  if (!reservationId || !child) {
    alert("Missing reservation ID or child data.");
    return;
  }

  // Validación de campos mínimos
  const requiredFields = ["first_name", "last_name"];
  const missingFields = requiredFields.filter(field => !child[field] || child[field].trim() === "");

  if (missingFields.length > 0) {
    alert(`Missing required fields for child #${index + 1}: ${missingFields.join(", ")}`);
    return;
  }

  if (!child.birth_date || isNaN(new Date(child.birth_date).getTime())) {
    console.log("ℹ️ No se informó fecha de nacimiento. Se omite validación de edad.");
  } else {
    try {
      const settingsRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/hotel-settings`, {
        params: { business_unit_id: businessUnitId }
      });
  
      const maxAgeChild = settingsRes.data.max_age_child;
      const maxAgeBaby = settingsRes.data.max_age_baby;
  
      const birthDate = new Date(child.birth_date);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const m = today.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
  
      console.log("🧠 Edad detectada:", age);
  
      if (age <= maxAgeBaby || age > maxAgeChild) {
        alert(`❌ Child must be older than ${maxAgeBaby} and equal or younger than ${maxAgeChild}.\nCurrent age: ${age}`);
        return;
      }
    } catch (error) {
      console.error("❌ Error checking age for child:", error);
      alert("Error validating child age.");
      return;
    }
  }
  

   

  try {
    console.log(`👶 Saving child #${index + 1}:`, child);
    let customerId;
    let response;

    if (child.id) {
      console.log(`✏️ Haciendo PUT al backend con id=${child.id}`);
      console.log("🧾 Datos enviados al backend:", child);
      response = await axios.put(`${process.env.REACT_APP_API_URL}/api/customers/${child.id}`, child);
      customerId = child.id;

      // ✅ Actualizar visualmente el slot del niño
      const updatedChildren = [...guestChildren];
      updatedChildren[index] = {
        ...child,
        id: customerId
      };
      setGuestChildren(updatedChildren);

    }
     else {
      // Cliente nuevo → crear
      response = await axios.post(`${process.env.REACT_APP_API_URL}/api/customers`, child);
      customerId = response.data.id;
    }

    const customerInDb = await axios.get(`${process.env.REACT_APP_API_URL}/api/customers/${customerId}`);
    console.log("🧾 Datos del customer en DB justo antes de insertarlo como reservation_guest:", customerInDb.data);

    // 🚨 Antes de insertar en reservation_guests, comprobar si ya existe
    try {
      const existingGuests = await axios.get(`${process.env.REACT_APP_API_URL}/api/reservation-drafts/draft-guests/by-draft/${reservationId}`);
      const alreadyLinked = existingGuests.data.some(g => g.customer_id === customerId);

      if (alreadyLinked) {
        console.log(`⚠️ Este niño (customer_id=${customerId}) ya está vinculado a la reserva. No se insertará de nuevo.`);
        setEditingChildIndex(null);
        setShowNewCustomerForm(false);
        return;
      }
    } catch (checkError) {
      console.error("❌ Error verificando existencia previa en reservation_guests:", checkError);
      alert("Unexpected error while checking existing guest.");
      return;
    }

    console.log(`✅ Child created in customers (ID: ${customerId})`);

    await axios.post(`${process.env.REACT_APP_API_URL}/api/reservation-drafts/draft-guests`, {
      reservation_draft_id: reservationId,
      customer_id: customerId,
      is_main_guest: false,
      is_child: true,
      guest_index: Number(index),
      relationship: null
    });
    

    console.log(`✅ Child #${index + 1} linked to reservation`);
    alert(`Child #${index + 1} saved successfully.`);

    // ✅ Cerrar formulario y limpiar edición
    setEditingChildIndex(null);
    setShowNewCustomerForm(false);

  } catch (error) {
    console.error("❌ Error saving child:", error);
    alert("Error saving child.");
  }
};


const handleRemoveAccompanyingGuest = async (index) => {
  const pathMatch = window.location.pathname.match(/\/new-reservation\/(\d+)/);
  const reservationId = pathMatch ? pathMatch[1] : null;
  const guest = guestCustomers[index];

  console.log("📦 guestCustomers antes de eliminar:", guestCustomers);
  console.log("👤 guest seleccionado en el index", index, ":", guest);

  if (!reservationId) {
    alert("Reservation ID missing");
    return;
  }

  
  // ✅ Obtener el customer_id real
  const customerId = guest.customer_id || guest.customer?.id || guest.id;
  if (!customerId) {
    alert("Error: Could not determine customer ID for deletion.");
    console.error("❌ guest sin customer_id válido:", guest);
    return;
  }

  try {
    await axios.delete(
      `${process.env.REACT_APP_API_URL}/api/reservation-drafts/draft-guests/by-draft/${reservationId}/by-customer/${customerId}?business_unit_id=${businessUnitId}`
    );

    console.log(`🗑️ Guest #${index + 1} eliminado de reservation_guests`);

    // Quitar del array visual
    const updated = [...guestCustomers];
    updated[index] = undefined; // 🛡️ Mantiene el slot vacío
    setGuestCustomers(updated);

    console.log("🔁 guestCustomers actualizado:", updated);

    
  } catch (error) {
    console.error("❌ Error removing accompanying guest:", error);
    alert("Error removing accompanying guest");
  }
};



const handleSelectAccompanyingGuest = (index) => {
  const pathMatch = window.location.pathname.match(/\/new-reservation\/(\d+)/);
  const reservationId = pathMatch ? pathMatch[1] : null;

  if (!reservationId) {
    alert("Reservation ID missing");
    return;
  }

  const businessUnitId = searchParams.get("business_unit_id") || "1";

  console.log("NAVEGAR acompañante", { guestIndex: index });
  navigate(
    `/select-customer?business_unit_id=${businessUnitId}&reservation_id=${reservationId}`,
    {
      state: {
        mode: "accompanying",
        guestIndex: index,
        customerIdFromURL
      }
    }
  );

};


return {
    handleAddNewAccompanyingGuest,
    handleAddNewChild,
    handleSelectChild,
    handleRemoveChild,
    handleSubmitChild,
    handleRemoveAccompanyingGuest,
    handleSelectAccompanyingGuest
  };
}